/**
 * "Add to chat" selections (board card p33g): text the user selected inside
 * an assistant reply, attached to the composer and sent as quoted context.
 *
 * Wire format — no contract change: on send the selections become leading
 * markdown blockquotes in the user message `content`, one paragraph per
 * selection, followed by a blank line and the user's own text:
 *
 *     > first selection
 *     > (second line of it)
 *
 *     > second selection
 *
 *     What does this mean?
 *
 * The agent receives exactly that (the bridge already carries the message
 * body as text, so quotes need no new part) and the stored message renders
 * the quotes above the text with `splitQuotedSelections`. A message without
 * selections is sent byte-for-byte as before.
 */

/** Longer selections are cut here (a note marks the cut). */
export const SELECTION_MAX_CHARS = 2_000

export interface ChatSelection {
  id: string
  text: string
  /** The selected text was longer than SELECTION_MAX_CHARS and was cut. */
  truncated: boolean
  /** Assistant message the text was selected in, when known. */
  messageId?: string
}

/** Marker appended to a truncated selection's last line. */
const TRUNCATED_MARK = '[…]'

/**
 * Clean up raw `Selection.toString()` output: normalise line endings, drop
 * trailing whitespace per line, collapse runs of blank lines, trim, and cap
 * the length at `maxChars` (cut at the last whitespace before the cap when
 * one exists in the final fifth, so words stay whole). Returns null when
 * nothing but whitespace was selected.
 */
export function normalizeSelectionText(
  raw: string,
  maxChars: number = SELECTION_MAX_CHARS
): { text: string; truncated: boolean } | null {
  const text = raw
    .replace(/\r\n?/g, '\n')
    .replace(/\u00a0/g, ' ')
    .split('\n')
    .map((line) => line.replace(/[ \t]+$/g, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
  if (!text) return null
  if (text.length <= maxChars) return { text, truncated: false }
  const head = text.slice(0, maxChars)
  const cutAt = head.search(/\s\S*$/)
  const cut = cutAt >= maxChars * 0.8 ? head.slice(0, cutAt) : head
  return { text: cut.trimEnd(), truncated: true }
}

/** Render one selection as a markdown blockquote paragraph. */
function quoteOne(selection: Pick<ChatSelection, 'text' | 'truncated'>): string {
  const lines = selection.text.split('\n')
  if (selection.truncated) lines[lines.length - 1] += ` ${TRUNCATED_MARK}`
  return lines.map((line) => (line ? `> ${line}` : '>')).join('\n')
}

/** The message content to send: quoted selections, a blank line, the text. */
export function quoteSelections(
  selections: readonly Pick<ChatSelection, 'text' | 'truncated'>[],
  text: string
): string {
  if (selections.length === 0) return text
  const quotes = selections.map(quoteOne).join('\n\n')
  const body = text.trim()
  return body ? `${quotes}\n\n${body}` : quotes
}

/**
 * Inverse of `quoteSelections` for rendering: the leading blockquote
 * paragraphs of a user message and the text that follows them. Content that
 * does not start with a blockquote comes back unchanged as `text`.
 */
export function splitQuotedSelections(content: string): { quotes: string[]; text: string } {
  if (!content.startsWith('>')) return { quotes: [], text: content }
  const lines = content.split('\n')
  const quotes: string[] = []
  let current: string[] | null = null
  let i = 0
  for (; i < lines.length; i++) {
    const line = lines[i]
    if (line.startsWith('>')) {
      current ??= []
      current.push(line.replace(/^> ?/, ''))
      continue
    }
    if (line.trim() === '' && current) {
      quotes.push(current.join('\n'))
      current = null
      // A blank line between quote paragraphs is fine; the first non-blank,
      // non-quote line ends the quoted head.
      let j = i + 1
      while (j < lines.length && lines[j].trim() === '') j++
      if (j < lines.length && lines[j].startsWith('>')) {
        i = j - 1
        continue
      }
      i = j
      break
    }
    break
  }
  if (current) quotes.push(current.join('\n'))
  return { quotes, text: lines.slice(i).join('\n').trim() }
}

/** Short one-line preview of a selection for chips and lists. */
export function selectionPreview(text: string, maxChars = 120): string {
  const oneLine = text.replace(/\s+/g, ' ').trim()
  return oneLine.length > maxChars ? `${oneLine.slice(0, maxChars - 1).trimEnd()}…` : oneLine
}

/** Parse what the draft store hands back (opaque JSON) into selections. */
export function parseDraftSelections(raw: unknown): ChatSelection[] {
  if (!Array.isArray(raw)) return []
  const out: ChatSelection[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const { id, text, truncated, messageId } = item as Record<string, unknown>
    if (typeof id !== 'string' || typeof text !== 'string' || !text) continue
    out.push({
      id,
      text,
      truncated: truncated === true,
      ...(typeof messageId === 'string' ? { messageId } : {}),
    })
  }
  return out
}
